import {
  useLoadScript,
  GoogleMap,
  Polygon,
  Polyline
} from '@react-google-maps/api'
import { GOOGLE_CONFIG } from '../../../config'
import { useEffect, useState } from 'react'
class LibraryRef {
  static librariesToLoad = ['geometry', 'places']
}

const drawingControlStyles = {
  container: {
    position: 'absolute',
    left: '10px',
    bottom: '24px',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px',
    borderRadius: '3px',
    backgroundColor: '#fff',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    fontFamily: 'Roboto, Arial, sans-serif'
  },
  button: {
    minHeight: '32px',
    padding: '0 10px',
    border: '1px solid #ccc',
    borderRadius: '2px',
    backgroundColor: '#fff',
    color: '#333',
    cursor: 'pointer',
    fontSize: '14px'
  },
  instructions: {
    maxWidth: '220px',
    color: '#333',
    fontSize: '12px'
  }
}

const previewOptions = {
  clickable: false,
  editable: false,
  fillColor: '#FF0000',
  fillOpacity: 0.2,
  strokeColor: '#FF0000',
  strokeOpacity: 0.8,
  strokeWeight: 2
}

const GenericMap = ({
  zoom,
  center,
  mapContainerStyle,
  onLoad,
  onUnmount,
  pathFinder,
  polygonEditVersion = 0,
  bucket,
  mapName
}) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_CONFIG.GOOGLE_MAP_API_KEY,
    libraries: LibraryRef.librariesToLoad,
    id: 'script-loader',
    language: 'en',
    region: 'us'
  })

  // Reloads the map to render updated content
  const [shouldRenderMap, setShouldRenderMap] = useState(true)
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawingPath, setDrawingPath] = useState([])
  const [editVersionAtStart, setEditVersionAtStart] = useState(0)
  const canFinishExistingEdit = drawingPath.length === 0 &&
    polygonEditVersion !== editVersionAtStart
  //  Whrere program saves shapes after being added
  const bucketTempChanges = bucket?.tempChanges?.data
  const bucketTempChangesDataKeys =
    bucketTempChanges && Object.keys(bucketTempChanges)
  const bucketDataLength =
    bucketTempChangesDataKeys && bucketTempChangesDataKeys.length
  // Where program loads shapes onLoad
  const bucketFullselection = bucket?.fullSelection
  const bucketFullselectionLength =
    bucketFullselection && bucketFullselection.length

  useEffect(() => {
    if (mapName && mapName === 'main') {
      setShouldRenderMap(false)
      setTimeout(() => setShouldRenderMap(true), 300)
    }

    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [bucketDataLength || bucketFullselectionLength])

  const startDrawing = () => {
    setDrawingPath([])
    setEditVersionAtStart(polygonEditVersion)
    setIsDrawing(true)
  }

  const cancelDrawing = () => {
    setDrawingPath([])
    setIsDrawing(false)
  }

  const undoLastPoint = () => {
    setDrawingPath(currentPath => currentPath.slice(0, -1))
  }

  const addDrawingPoint = event => {
    if (!isDrawing || !event.latLng) return

    setDrawingPath(currentPath => [
      ...currentPath,
      { lat: event.latLng.lat(), lng: event.latLng.lng() }
    ])
  }

  const finishDrawing = () => {
    if (canFinishExistingEdit) {
      // Existing overlays already relay their updated coordinates to the editor.
      cancelDrawing()
      return
    }
    if (drawingPath.length < 3) return

    const overlay = new window.google.maps.Polygon({ paths: drawingPath })
    pathFinder({ type: 'polygon', overlay })
    setDrawingPath([])
    setIsDrawing(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      {!isLoaded ? (
        <h1>Loading...</h1>
      ) : (
        shouldRenderMap && (
          <GoogleMap
            zoom={zoom}
            center={center}
            mapContainerStyle={mapContainerStyle}
            options={{ disableDoubleClickZoom: isDrawing }}
            version="weekly"
            onLoad={onLoad}
            onUnmount={onUnmount}
            onClick={pathFinder ? addDrawingPoint : undefined}
          >
            {drawingPath.length > 1 && (
              <Polyline path={drawingPath} options={previewOptions} />
            )}
            {drawingPath.length > 2 && (
              <Polygon paths={drawingPath} options={previewOptions} />
            )}
          </GoogleMap>
        )
      )}
      {isLoaded && shouldRenderMap && pathFinder && (
        <div style={drawingControlStyles.container}>
          {!isDrawing ? (
            <button
              type='button'
              style={drawingControlStyles.button}
              onClick={startDrawing}
            >
              Draw polygon
            </button>
          ) : (
            <>
              <span style={drawingControlStyles.instructions}>
                Click the map to add at least three points, or drag the handles of an existing polygon to adjust it.
              </span>
              <button
                type='button'
                style={drawingControlStyles.button}
                onClick={undoLastPoint}
                disabled={!drawingPath.length}
              >
                Undo point
              </button>
              <button
                type='button'
                style={drawingControlStyles.button}
                onClick={cancelDrawing}
              >
                Cancel
              </button>
              <button
                type='button'
                style={drawingControlStyles.button}
                onClick={finishDrawing}
                disabled={drawingPath.length < 3 && !canFinishExistingEdit}
              >
                Finish polygon
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default GenericMap
